import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  IconButton,
  FormHelperText,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ReactSelect from 'react-select';
import { PageTitleContainer, RichTextEditor } from '../components/common';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useProject from '../hooks/useProject';
import useNotification from '../hooks/useNotification';
import useSection from '../hooks/useSection';
import sectionAvatarService from '../services/sectionAvatarService';
import ImageAvatarSelector from '../components/layout/ImageAvatarSelector';
import SectionedTaskSelector from '../components/projects/SectionedTaskSelectorImproved';
import RepositorySelectionStep from '../components/projects/RepositorySelectionStep';
import WebsiteRequirementsForm from '../components/projects/WebsiteRequirementsForm';
import WebsiteRequirementsWizard from '../components/projects/WebsiteRequirementsWizard';
import userService from '../services/userService';
import teamService from '../services/teamService';
import projectService from '../services/projectService';
import AdbIcon from '@mui/icons-material/Adb';

// Icons for rich text editor toolbar
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import TableChartIcon from '@mui/icons-material/TableChart';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import EventIcon from '@mui/icons-material/Event';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SecurityIcon from '@mui/icons-material/Security';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import SettingsIcon from '@mui/icons-material/Settings';
import ApiIcon from '@mui/icons-material/Api';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';
import PersonIcon from '@mui/icons-material/Person';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GroupsIcon from '@mui/icons-material/Groups';
import DescriptionIcon from '@mui/icons-material/Description';
import { CodeIcon } from '../components/icons';
import ChatIcon from '@mui/icons-material/Chat';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SearchIcon from '@mui/icons-material/Search';
import CreateIcon from '@mui/icons-material/Create';
import VerifiedIcon from '@mui/icons-material/Verified';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HandshakeIcon from '@mui/icons-material/Handshake';

// Define workflow tiles for standard project types
const standardWorkflowTiles = [
  {
    id: 'chat-prd',
    title: 'Project Info',
    icon: <ChatIcon fontSize="large" />,
    description: 'Chat with your PRD to get insights and clarifications'
  },
  {
    id: 'extract-prd',
    title: 'Extract PRD',
    icon: <DescriptionIcon fontSize="large" />,
    description: 'Extract PRD content from conversations and documents'
  },
  {
    id: 'field-validations',
    title: 'Field Validations',
    icon: <ListAltIcon fontSize="large" />,
    description: 'Define field validation rules for all inputs'
  },
  {
    id: 'tech-stack',
    title: 'Technology Stack',
    icon: <SettingsIcon fontSize="large" />,
    description: 'Define the technology stack for the project'
  },
  {
    id: 'dev-guidelines',
    title: 'Development Guidelines',
    icon: <CodeIcon fontSize="large" />,
    description: 'Set coding standards and development practices'
  },
  {
    id: 'design-guidelines',
    title: 'Design Guidelines',
    icon: <DesignServicesIcon fontSize="large" />,
    description: 'Define UI/UX design principles and standards'
  },
  {
    id: 'security-guidelines',
    title: 'Security Guidelines',
    icon: <SecurityIcon fontSize="large" />,
    description: 'Define security requirements and practices'
  },
  {
    id: 'api-guidelines',
    title: 'API Guidelines',
    icon: <ApiIcon fontSize="large" />,
    description: 'Set standards for API design and implementation'
  },
  {
    id: 'features',
    title: 'Features',
    icon: <FeaturedPlayListIcon fontSize="large" />,
    description: 'Define core features and functionality'
  },
  {
    id: 'user-stories',
    title: 'User Stories',
    icon: <PersonIcon fontSize="large" />,
    description: 'Create user stories for each feature'
  },
  {
    id: 'architecture',
    title: 'Architecture',
    icon: <ArchitectureIcon fontSize="large" />,
    description: 'Generate system architecture diagrams and patterns'
  },
  {
    id: 'flow',
    title: 'Process Flows',
    icon: <AccountTreeIcon fontSize="large" />,
    description: 'Generate sequence diagrams and process flows'
  },
  {
    id: 'user-roles',
    title: 'User Roles',
    icon: <GroupsIcon fontSize="large" />,
    description: 'Define user roles and permissions'
  },
  {
    id: 'final-prd',
    title: 'Final PRD',
    icon: <DescriptionIcon fontSize="large" />,
    description: 'Compile all sections into the final PRD document'
  },
  {
    id: 'development',
    title: 'Development',
    icon: <CodeIcon fontSize="large" />,
    description: 'Deploy and develop the application'
  },
  {
    id: 'acdd',
    title: 'ACDD',
    icon: <ArchitectureIcon fontSize="large" />,
    description: 'Application Component Design Document'
  },
];

// Define workflow tiles for agentic crew projects
const agenticCrewWorkflowTiles = [
  {
    id: 'agentic-crew-info',
    title: 'Agentic Crew Info',
    icon: <SmartToyIcon fontSize="large" />,
    description: 'Define your agentic crew scope and initialization method'
  },
  {
    id: 'features',
    title: 'Features',
    icon: <FeaturedPlayListIcon fontSize="large" />,
    description: 'Define core features and capabilities the crew will handle'
  },
  {
    id: 'user-stories',
    title: 'User Stories',
    icon: <PersonIcon fontSize="large" />,
    description: 'Create user stories for crew interactions and outcomes'
  },
  {
    id: 'user-roles',
    title: 'User Roles',
    icon: <GroupsIcon fontSize="large" />,
    description: 'Define user roles and permissions when crew is deployed'
  },
  {
    id: 'process-flows',
    title: 'Process Flows',
    icon: <AccountTreeIcon fontSize="large" />,
    description: 'Map out agent decision-making and workflow patterns'
  },
  {
    id: 'input-sources',
    title: 'Input Sources',
    icon: <ArchitectureIcon fontSize="large" />,
    description: 'Define where the crew gets its input data and triggers'
  },
  {
    id: 'agents-orchestrators',
    title: 'Agents & Orchestrators',
    icon: <SmartToyIcon fontSize="large" />,
    description: 'Design agent roles and orchestration patterns'
  },
  {
    id: 'finetuning-activities',
    title: 'Finetuning Activities',
    icon: <SettingsIcon fontSize="large" />,
    description: 'Configure agent learning and optimization activities'
  },
  {
    id: 'frequency',
    title: 'Frequency',
    icon: <EventIcon fontSize="large" />,
    description: 'Set execution frequency and scheduling patterns'
  },
  {
    id: 'output',
    title: 'Output',
    icon: <DescriptionIcon fontSize="large" />,
    description: 'Define output format: email, knowledge hub, dashboard, etc.'
  },
  {
    id: 'ui-design',
    title: 'UI/UX Design',
    icon: <DesignServicesIcon fontSize="large" />,
    description: 'Design interface when output requires an application'
  },
  {
    id: 'tools-integration',
    title: 'Tools & Integration',
    icon: <IntegrationInstructionsIcon fontSize="large" />,
    description: 'Configure tools like web search, APIs, and integrations'
  },
];

// Function to get appropriate workflow tiles based on application type
const getWorkflowTiles = (applicationType) => {
  if (applicationType === 'agentic-crew') {
    return agenticCrewWorkflowTiles;
  }
  return standardWorkflowTiles;
};

const applicationTypes = [
  { id: 'agentic-crew', name: 'Agentic Crew' },
  { id: 'mobile', name: 'Mobile App' },
  { id: 'webapplication-microservices', name: 'Web Application - Microservices' },
  { id: 'webapplication-monolithic', name: 'Web Application - Monolithic' },
  { id: 'website', name: 'Website' },
];

const CreateProject = ({ projectId: propProjectId, project, editMode = false, onClose, showHeader = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { createProject, updateProject, getProject, loading, error: projectError } = useProject();
  const { showNotification } = useNotification();
  const { sections, isLoadingSections, sectionsError, refetchSections } = useSection();
  
  // Get projectId from URL query params if present
  const queryParams = new URLSearchParams(location.search);
  const urlProjectId = queryParams.get('projectId');
  const projectType = queryParams.get('type');
  const workflowType = queryParams.get('workflow');
  
  // Use projectId from props or URL
  const projectId = propProjectId || urlProjectId;

  // Refs for height calculation
  const contentRef = useRef(null);
  const mainHeaderRef = useRef(null);
  const titleHeaderRef = useRef(null);

  // State for content height
  const [contentHeight, setContentHeight] = useState('calc(100vh - 130px)');


  // Form state
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [applicationType, setApplicationType] = useState(projectType || 'webapplication-monolithic');
  const [workflowMode, setWorkflowMode] = useState(workflowType || 'scratch');
  const [shortCode, setShortCode] = useState('');
  const [projectOwner, setProjectOwner] = useState('');
  const [projectCoOwners, setProjectCoOwners] = useState([]); // Changed to array for multi-select
  const [team, setTeam] = useState('');
  const [distributionEmail, setDistributionEmail] = useState('');
  const [customer, setCustomer] = useState('');

  // Data for dropdowns
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);

  // State to track if we're in edit mode from URL
  const [isUrlEditMode, setIsUrlEditMode] = useState(false);
  
  // Helper function to populate project data
  const populateProjectData = (projectData) => {
    try {
      console.log('Setting project form data from:', projectData);
      console.log('Current application type:', projectData.applicationType);

      // Set form fields from project data with better debugging
      setProjectName(projectData.name || '');
      console.log('Set project name:', projectData.name);
      
      setProjectDescription(projectData.description || '');
      console.log('Set project description:', projectData.description);
      
      setApplicationType(projectData.applicationType || 'website');
      console.log('Set application type:', projectData.applicationType);
      
      setShortCode(projectData.shortCode || '');
      console.log('Set short code:', projectData.shortCode);

      // Handle owner - may be an object or string ID
      console.log('Raw owner data:', projectData.owner);
      if (projectData.owner) {
        const ownerId = typeof projectData.owner === 'object' ?
          projectData.owner._id : projectData.owner;
        console.log('Setting project owner to:', ownerId);
        setProjectOwner(ownerId || '');
      } else {
        console.log('No owner field found in project data');
      }

      // Handle co-owners as an array
      console.log('Raw coOwners data:', projectData.coOwners);
      if (projectData.coOwners && Array.isArray(projectData.coOwners)) {
        const coOwnerIds = projectData.coOwners.map(co => {
          console.log('Processing coOwner:', co);
          return typeof co === 'object' ? co._id : co;
        });
        console.log('Setting project co-owners from coOwners array:', coOwnerIds);
        setProjectCoOwners(coOwnerIds);
      } else if (projectData.coOwner) {
        // For backward compatibility
        console.log('Raw coOwner data (legacy field):', projectData.coOwner);
        const coOwnerId = typeof projectData.coOwner === 'object' ?
          projectData.coOwner._id : projectData.coOwner;
        console.log('Setting project co-owner from legacy field:', coOwnerId);
        setProjectCoOwners([coOwnerId]);
      } else {
        console.log('No co-owners found, setting empty array');
        setProjectCoOwners([]);
      }
    } catch (error) {
      console.error('Error setting project data:', error);
      showNotification({
        message: `Error processing project data: ${error.message}`,
        type: 'error'
      });
    }

    try {
      // Handle team - may be an object or string ID
      console.log('Raw team data:', projectData.team);
      if (projectData.team) {
        const teamId = typeof projectData.team === 'object' ?
          projectData.team._id : projectData.team;
        console.log('Setting team to:', teamId);
        setTeam(teamId || '');
      } else {
        console.log('No team field found in project data');
      }

      setDistributionEmail(projectData.distributionEmail || '');
      console.log('Set distribution email:', projectData.distributionEmail);

      // Handle customer - may be an object or string ID
      console.log('Raw customer data:', projectData.customer);
      if (projectData.customer) {
        const customerId = typeof projectData.customer === 'object' ?
          projectData.customer._id : projectData.customer;
        console.log('Setting customer to:', customerId);
        setCustomer(customerId || '');
      } else {
        console.log('No customer field found in project data');
      }

      if (projectData.avatar) {
        console.log('Setting avatar:', projectData.avatar);
        setSelectedAvatar(projectData.avatar);
        setAvatarName(projectData.avatar.name || '');
      }

      console.log('Project form data set successfully');
    } catch (error) {
      console.error('Error setting additional project data:', error);
      showNotification({
        message: `Error processing additional project data: ${error.message}`,
        type: 'error'
      });
    }
  };
  
  // Check if projectId is in URL and update edit mode
  useEffect(() => {
    console.log('=== DEBUG === URL projectId check triggered. urlProjectId:', urlProjectId, 'editMode:', editMode);
    
    // Force a state update to ensure edit mode is set
    if (urlProjectId) {
      console.log('Project ID found in URL, enabling edit mode regardless of current state:', urlProjectId);
      setIsUrlEditMode(true);
      
      // Immediately fetch project data without waiting for state update
      const fetchProjectImmediately = async () => {
        try {
          console.log('=== IMMEDIATE FETCH === Directly calling getProjectWithDetails');
          const detailedData = await projectService.getProjectWithDetails(urlProjectId);
          
          if (detailedData && detailedData.project) {
            console.log('=== IMMEDIATE FETCH === Successfully retrieved project data, populating form');
            populateProjectData(detailedData.project);
          } else {
            console.warn('=== IMMEDIATE FETCH === No project data received');
          }
        } catch (error) {
          console.error('=== IMMEDIATE FETCH === Error fetching project data:', error);
        }
      };
      
      fetchProjectImmediately();
    } else {
      console.log('No URL projectId present');
    }
  }, [urlProjectId, showNotification]);
  
  // If in edit mode, load the project data
  useEffect(() => {
    const isInEditMode = editMode || isUrlEditMode;
    console.log('=== DEBUG === CreateProject useEffect triggered');
    console.log('CreateProject useEffect for projectId:', projectId, 'editMode:', isInEditMode, 'urlEditMode:', isUrlEditMode, 'direct project data:', project);

    // Skip URL-based projectId loading since we're handling that in a separate useEffect
    if (isInEditMode && project) {
      console.log('Using directly passed project data');
      populateProjectData(project);
    } else if (isInEditMode && projectId && !urlProjectId) {
      // Only fetch if this is prop-based projectId, not URL-based (which is handled separately)
      console.log('=== DEBUG === Direct prop-based project fetching for ID:', projectId);
      
      // Fetch from API using projectId
      const fetchProject = async () => {
        console.log('=== DEBUG === fetchProject function called with projectId:', projectId);
        try {
          console.log('Fetching detailed project data for ID:', projectId);
          
          // Use the new specialized API endpoint for detailed project data
          const detailedData = await projectService.getProjectWithDetails(projectId);
          console.log('Detailed project data retrieved:', detailedData);
          
          if (detailedData && detailedData.project) {
            // Use the project data from the detailed response
            populateProjectData(detailedData.project);
            
            // Process team details if available
            if (detailedData.teamDetails) {
              console.log('Team details available:', detailedData.teamDetails);
              // Additional team-specific processing could be done here
            }
          } else {
            console.warn('No detailed project data received from API, falling back to regular endpoint');
            // Fallback to the regular getProject method
            const projectData = await getProject(projectId);
            console.log('Basic project data retrieved as fallback:', projectData);
            
            if (projectData) {
              populateProjectData(projectData);
            } else {
              console.warn('No project data received from API');
            }
          }
        } catch (error) {
          console.error('Error fetching project details:', error);
          showNotification({
            message: `Error loading project: ${error.message}`,
            type: 'error'
          });
          
          // Attempt fallback to regular getProject
          try {
            console.log('Attempting fallback to regular project endpoint');
            const projectData = await getProject(projectId);
            
            if (projectData) {
              populateProjectData(projectData);
            }
          } catch (fallbackError) {
            console.error('Fallback also failed:', fallbackError);
          }
        }
      };

      console.log('=== DEBUG === About to call fetchProject function');
      fetchProject();
      console.log('=== DEBUG === Called fetchProject function');
    } else {
      console.log('=== DEBUG === Not fetching project because:', 
        !isInEditMode ? 'not in edit mode' : 
        (urlProjectId ? 'URL-based project ID is handled separately' :
        !project && !projectId ? 'no project or projectId' : 'unknown reason'));
    }
  }, [editMode, projectId, project, getProject, showNotification, isUrlEditMode, urlProjectId]);

  // Log props for debugging
  useEffect(() => {
    console.log("CreateProject component received props:", { projectId, project, editMode });
  }, [projectId, project, editMode]);
  
  // Log all form state for debugging
  useEffect(() => {
    console.log("Current form state:", {
      projectName,
      projectDescription,
      applicationType,
      shortCode,
      projectOwner,
      projectCoOwners,
      team, 
      distributionEmail,
      customer
    });
  }, [
    projectName,
    projectDescription,
    applicationType,
    shortCode,
    projectOwner,
    projectCoOwners,
    team,
    distributionEmail,
    customer
  ]);

  // Fetch users and teams for dropdowns
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        // Fetch users
        setLoadingUsers(true);
        const usersData = await userService.getAllUsers();
        console.log('Fetched users:', usersData);

        // Add detailed logging to debug user data fields
        console.log('Raw user data first item:', usersData.length > 0 ? usersData[0] : 'No users');

        // Process users to extract first/last names from name field
        const formattedUsers = usersData.map(user => {
          // Extract firstName and lastName from name field if available
          let firstName = '';
          let lastName = '';

          if (user.name) {
            // Split the name field into first and last name
            const nameParts = user.name.split(' ');
            if (nameParts.length > 1) {
              firstName = nameParts[0];
              lastName = nameParts.slice(1).join(' ');
            } else {
              firstName = user.name;
            }
          } else if (user.firstName || user.lastName) {
            // Use explicit firstName/lastName if available
            firstName = user.firstName || '';
            lastName = user.lastName || '';
          }

          const fullName = `${firstName} ${lastName}`.trim();
          const label = fullName || user.name || user.username || user.email || 'Unknown User';

          console.log('User:', user._id, 'name:', user.name, 'email:', user.email, 'formatted as:', label);

          return {
            value: user._id,
            label: label
          };
        });

        console.log('Formatted users for dropdown:', formattedUsers);
        setUsers(formattedUsers);
        setLoadingUsers(false);

        // Fetch teams
        setLoadingTeams(true);
        const teamsData = await teamService.getUserTeams();
        console.log('Fetched teams:', teamsData);

        // Transform teams for react-select
        const formattedTeams = (teamsData.data || []).map(team => ({
          value: team._id,
          label: team.name
        }));
        setTeams(formattedTeams);
        setLoadingTeams(false);
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
        showNotification({
          message: `Error loading users or teams: ${error.message}`,
          type: 'error'
        });
        setLoadingUsers(false);
        setLoadingTeams(false);
      }
    };

    fetchDropdownData();
  }, [showNotification]);

  // Calculate content height based on window size and header heights
  useLayoutEffect(() => {
    const updateContentHeight = () => {
      // Default header heights (fallback values if refs aren't available)
      const mainHeaderHeight = 73;  // Main app header
      const titleHeaderHeight = 80; // Create project header
      const extraPadding = 69;      // Additional padding for spacing

      const totalHeaderHeight = mainHeaderHeight + titleHeaderHeight + extraPadding;
      setContentHeight(`calc(100vh - ${totalHeaderHeight}px)`);
    };

    // Initial calculation
    updateContentHeight();

    // Re-calculate on window resize
    window.addEventListener('resize', updateContentHeight);

    return () => {
      window.removeEventListener('resize', updateContentHeight);
    };
  }, []);


  // Auto-generate shortCode from project name when it changes
  useEffect(() => {
    if (projectName && !shortCode) {
      const words = projectName.split(/\s+/).filter(word => word.length > 0);
      let generatedCode = '';

      if (words.length === 1) {
        const word = words[0];
        generatedCode = word.substring(0, Math.min(2, word.length)).toUpperCase();
      } else if (words.length >= 2) {
        generatedCode = (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
      }

      if (generatedCode) {
        setShortCode(generatedCode);
      }
    }
  }, [projectName, shortCode]);

  // Editor state
  const [editorMode, setEditorMode] = useState('normal'); // 'normal', 'bold', 'italic', 'link'
  const [showAIOptions, setShowAIOptions] = useState(false);

  // Avatar state
  const [avatarName, setAvatarName] = useState('');
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);

  // Task selection state
  const [showTaskSelection, setShowTaskSelection] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [allTaskIds, setAllTaskIds] = useState([]);
  const [visibleTaskIds, setVisibleTaskIds] = useState([]); // Track actually visible tasks on page
  const [createdProjectId, setCreatedProjectId] = useState(null);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  
  // Repository selection state
  const [showRepositorySelection, setShowRepositorySelection] = useState(false);
  
  // Website requirements state - removed since we handle it on a separate page

  // Form validation
  const [nameError, setNameError] = useState('');
  const [ownerError, setOwnerError] = useState('');
  const [error, setError] = useState('');

  // Fetch available tasks from API when task selection screen is shown or application type changes
  useEffect(() => {
    if (showTaskSelection) {
      fetchAvailableTasks();
    }
  }, [showTaskSelection, refetchSections, applicationType]);

  // Collect all available task IDs for select all functionality
  useEffect(() => {
    if (sections && sections.length > 0) {
      const allIds = [];
      sections.forEach(section => {
        if (section.tasks && section.tasks.length > 0) {
          section.tasks.forEach(task => {
            if (task) {
              const taskId = task.id || task._id || task.title;
              if (taskId) {
                allIds.push(taskId);
              }
            }
          });
        }
      });
      console.log('All available task IDs:', allIds);
      setAllTaskIds(allIds);
    }
  }, [sections]);

  // Function to fetch available tasks
  const fetchAvailableTasks = async () => {
    try {
      setLoadingTasks(true);

      // Log current sections before refetching
      console.log('Current sections before refetch:', sections);

      try {
        // Refetch sections and tasks from the API
        await refetchSections();

        console.log('Sections after refetch:', sections);

        // Wait a moment for sections data to be updated
        setTimeout(() => {
          if (sections && sections.length > 0) {
            // Filter sections based on application type
            const filteredSections = sections.filter(section => {
              if (applicationType === 'agentic-crew') {
                // For agentic crew projects, only show agentic sections
                return section.unique_name && section.unique_name.startsWith('agentic-');
              } else {
                // For other project types, exclude agentic sections
                return !section.unique_name || !section.unique_name.startsWith('agentic-');
              }
            });

            console.log(`Application type: ${applicationType}`);
            console.log(`Filtered ${filteredSections.length} sections from ${sections.length} total sections`);

            // Extract all tasks from filtered sections
            const allTasks = [];
            filteredSections.forEach(section => {
              if (section.tasks && Array.isArray(section.tasks)) {
                section.tasks.forEach(task => {
                  if (task && task.id) {
                    allTasks.push({
                      id: task.id,
                      title: task.title || task.id,
                      description: task.description || '',
                      icon: task.icon || 'CodeIcon',
                      section: section._id
                    });
                  }
                });
              }
            });

            if (allTasks.length > 0) {
              console.log(`Found ${allTasks.length} tasks from filtered sections`);
              setAvailableTasks(allTasks);
            } else {
              console.log('No tasks found in filtered sections, using default tasks');
              setAvailableTasks(getWorkflowTiles(applicationType));
            }
          } else {
            console.log('No sections found, using default tiles');
            setAvailableTasks(getWorkflowTiles(applicationType));
          }

          setLoadingTasks(false);
        }, 500); // Small delay to ensure state is updated
      } catch (fetchError) {
        console.error('Error during refetchSections:', fetchError);
        setAvailableTasks(getWorkflowTiles(applicationType));
        setLoadingTasks(false);
      }
    } catch (err) {
      console.error('Error fetching available tasks:', err);
      showNotification({
        message: 'Failed to load available tasks',
        type: 'error'
      });
      setLoadingTasks(false);

      // Fallback to default tiles if API fails
      setAvailableTasks(getWorkflowTiles(applicationType));
    }
  };

  const validateProjectName = () => {
    if (!projectName.trim()) {
      setNameError('This field is required');
      return false;
    }
    if (projectName.length < 3) {
      setNameError('Project name must be at least 3 characters');
      return false;
    }
    setNameError('');
    return true;
  };

  const validateProjectOwner = () => {
    if (!projectOwner) {
      setOwnerError('This field is required');
      return false;
    }
    setOwnerError('');
    return true;
  };

  const handleCreateProject = async () => {
    const isNameValid = validateProjectName();
    const isOwnerValid = validateProjectOwner();
    
    if (!isNameValid || !isOwnerValid) {
      return;
    }

    try {
      setError('');

      const projectData = {
        name: projectName,
        description: projectDescription,
        templateId: 'blank',
        applicationType: applicationType,
        shortCode: shortCode,
        owner: projectOwner, // Already contains user ID
        coOwners: projectCoOwners, // Array of user IDs
        team: team, // Already contains team ID
        distributionEmail: distributionEmail,
        customer: customer
      };
      
      console.log('Submitting project data:', JSON.stringify(projectData, null, 2));

      // Set default avatar data (this won't be displayed but prevents backend errors)
      projectData.avatar = {
        name: "Default Avatar",
        photoUrl: "/assets/avatars/face_co (1) 4.png",
        type: "robot",
        backgroundColor: "#e3f2fd"
      };

      if ((editMode || isUrlEditMode) && projectId) {
        // Update existing project
        // console.log("Updating project with ID:", projectId);
        // console.log("Update payload - owner:", projectData.owner);
        // console.log("Update payload - coOwners:", projectData.coOwners);
        
        const updatedProject = await updateProject({ projectId, projectData });
        // console.log("Updated project response:", updatedProject);

        showNotification({
          message: 'Project updated successfully!',
          type: 'success'
        });

        // Close if in dialog mode, otherwise navigate back
        if (onClose) {
          onClose(true);  // Pass true to indicate successful save
        } else {
          navigate(`/projects/${projectId}`);
        }
      } else {
        // Create new project
        const newProject = await createProject(projectData);

        showNotification({
          message: 'Project created successfully!',
          type: 'success'
        });

        // Handle different workflow types
        const newProjectId = newProject._id;
        setCreatedProjectId(newProjectId);

        // Clear everything for this project first
        console.log(`New project created: ${newProjectId}. Preparing orchestrators...`);
        sectionAvatarService.clearProjectAvatars(newProjectId);

        // Mark this as a newly created project in session storage
        // to ensure avatars start empty with default orchestrator names
        sessionStorage.setItem('newly_created_project', newProjectId);
        console.log(`Set newly_created_project flag in session storage for project: ${newProjectId}`);

        // List all avatar storage after clearing
        sectionAvatarService.listAllAvatarStorage();

        // Route based on workflow type and application type
        if (applicationType === 'website') {
          // Website projects navigate to requirements wizard page
          navigate(`/projects/${newProjectId}/website-requirements`);
        } else if (workflowMode === 'scratch') {
          // Scratch: Show task selection, then go to foundation (no code linking)
          setShowTaskSelection(true);
        } else if (workflowMode === 'existing' || workflowMode === 'ms') {
          // Existing/M&S: Show repository selection first, then task selection, then foundation
          setShowRepositorySelection(true);
        } else {
          // Default: Show task selection
          setShowTaskSelection(true);
        }
      }
    } catch (err) {
      console.error(editMode ? 'Error updating project:' : 'Error creating project:', err);
      setError(err.message || `Failed to ${editMode ? 'update' : 'create'} project. Please try again.`);
    }
  };

  const handleTaskSelection = (taskId) => {
    console.log('handleTaskSelection called with taskId:', taskId);
    setSelectedTasks(prev => {
      console.log('Previous selectedTasks:', prev);
      const newTasks = prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId];
      console.log('New selectedTasks:', newTasks);
      return newTasks;
    });
  };

  // Handle select all tasks
  const handleSelectAll = () => {
    // Use the actual visible tasks on the page (not all tasks from state)
    const tasksToSelect = visibleTaskIds;
    
    // If all visible tasks are already selected, deselect all
    if (selectedTasks.length === tasksToSelect.length &&
        tasksToSelect.length > 0 &&
        tasksToSelect.every(id => selectedTasks.includes(id))) {
      setSelectedTasks([]);
    } else {
      // Select all visible tasks on the page
      setSelectedTasks([...tasksToSelect]);
    }
  };

  const handleFinishTaskSelection = async () => {
    try {
      console.log('handleFinishTaskSelection - selectedTasks:', selectedTasks);
      console.log('handleFinishTaskSelection - selectedTasks.length:', selectedTasks.length);
      console.log('handleFinishTaskSelection - createdProjectId:', createdProjectId);
      
      // Update the project with the selected tasks
      if (createdProjectId && selectedTasks.length > 0) {
        showNotification({
          message: 'Saving selected tasks...',
          type: 'info'
        });

        // Filter out any null or undefined values
        const cleanTasks = selectedTasks.filter(task => task != null && task !== undefined && task !== '');
        console.log('Clean tasks after filtering:', cleanTasks);

        // Update the project with the selected tasks
        // The updateProject requires an object with projectId and projectData
        await updateProject({
          projectId: createdProjectId,
          projectData: { tasks: cleanTasks }
        });

        showNotification({
          message: `${cleanTasks.length} ${cleanTasks.length === 1 ? 'topic' : 'topics'} activated successfully!`,
          type: 'success'
        });
      } else {
        console.log('Not saving tasks - either no project ID or no tasks selected');
        showNotification({
          message: 'No tasks selected to activate',
          type: 'info'
        });
      }

      // Log all avatar storage before completion
      console.log("Project setup completed, checking final avatar state:");
      sectionAvatarService.listAllAvatarStorage();

      // Clear the newly created project flag from session storage
      // But keep the avatars so they persist in the workflow
      console.log(`Removing newly_created_project flag for project: ${createdProjectId}`);
      sessionStorage.removeItem('newly_created_project');
      
      // Route based on workflow type
      setShowTaskSelection(false);
      
      // After task selection, always go to foundation page
      // Repository selection (code linking) happens before task selection for existing/ms workflows
      navigate(`/projects/${createdProjectId}/prd-workflow#foundation`);
    } catch (err) {
      console.error('Error saving selected tasks:', err);
      showNotification({
        message: `Error: ${err.message || "Failed to save tasks"}`,
        type: 'error'
      });
    }
  };

  const handleOpenAvatarDialog = () => {
    setAvatarDialogOpen(true);
  };

  const handleCloseAvatarDialog = () => {
    setAvatarDialogOpen(false);
  };

  const handleSelectAvatar = (avatar) => {
    setSelectedAvatar(avatar);
    setAvatarDialogOpen(false);
  };

  // Description editor functions
  const handleFormatText = (format) => {
    setEditorMode(format);

    switch (format) {
      case 'bold':
        setProjectDescription(projectDescription + '**bold text**');
        break;
      case 'italic':
        setProjectDescription(projectDescription + '*italic text*');
        break;
      case 'link':
        setProjectDescription(projectDescription + '[link text](url)');
        break;
      case 'list':
        setProjectDescription(projectDescription + '\n- List item 1\n- List item 2\n- List item 3');
        break;
      case 'numbered-list':
        setProjectDescription(projectDescription + '\n1. First item\n2. Second item\n3. Third item');
        break;
      case 'table':
        setProjectDescription(projectDescription + '\n\n| Header 1 | Header 2 |\n| --- | --- |\n| Row 1, Col 1 | Row 1, Col 2 |\n| Row 2, Col 1 | Row 2, Col 2 |');
        break;
      default:
        break;
    }
  };

  const handleAIOptions = () => {
    setShowAIOptions(!showAIOptions);
  };

  const handleAIGenerate = (option) => {
    // In a real implementation, this would call an AI service
    let aiText = '';

    switch (option) {
      case 'summary':
        aiText = 'This project aims to create a comprehensive solution that addresses key user needs while maintaining performance and scalability.';
        break;
      case 'features':
        aiText = '**Key Features:**\n\n- Intuitive user interface\n- Robust authentication system\n- Real-time data synchronization\n- Advanced analytics dashboard\n- Mobile-responsive design';
        break;
      case 'goals':
        aiText = '**Project Goals:**\n\n1. Improve user retention by 30%\n2. Reduce loading times by 50%\n3. Expand market reach to new demographics\n4. Implement industry-leading security standards';
        break;
      default:
        break;
    }

    setProjectDescription(projectDescription ? projectDescription + '\n\n' + aiText : aiText);
    setShowAIOptions(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box
      className="CreateProject-root"
      sx={{
        width: '100%',
        bgcolor: editMode ? 'white' : '#f5f6f7',
        minHeight: editMode ? 'auto' : '100vh',
        pb: 4,
        px: editMode ? 0 : 6,
        pt: editMode ? 0 : 4,
        overflowX: 'hidden',
        backgroundImage: 'none', // Explicitly remove any background image
        '&::before': { display: 'none' },
        '&::after': { display: 'none' }
      }}>
      {/* Project Header Section with standardized styling */}
      {/* Show header if showHeader prop is true */}
      {showHeader && (!editMode && !isUrlEditMode) && (
        <PageTitleContainer
          ref={titleHeaderRef}
          title={showTaskSelection ? 'Select the topics you want to activate for your project' : 
                (isUrlEditMode ? 'Edit project' : 'Create project')}
          justifyContent={'space-between'}
          rightContent={
            showTaskSelection && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedTasks.length === visibleTaskIds.length && visibleTaskIds.length > 0 &&
                      visibleTaskIds.every(id => selectedTasks.includes(id))}
                    onChange={handleSelectAll}
                    sx={{
                      color: '#475969',
                      '&.Mui-checked': {
                        color: '#475969',
                      },
                      ml: 3
                    }}
                  />
                }
                label={(() => {
                  const isAllSelected = selectedTasks.length === visibleTaskIds.length && visibleTaskIds.length > 0 &&
                    visibleTaskIds.every(id => selectedTasks.includes(id));
                  const action = isAllSelected ? "Deselect All" : "Select All";
                  const count = `(${selectedTasks.length}/${visibleTaskIds.length})`;
                  return `${action} ${count}`;
                })()}
                sx={{
                  '& .MuiTypography-root': {
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#475969'
                  }
                }}
              />
            )
          }
          sx={{
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            ml: 0,
            mr: 0,
            mb: 2,
            mt: 0,
            py: '16px' // Exact 16px padding top and bottom
          }}
          titleProps={{
            sx: { fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }
          }}
        />
      )}

      {/* Show title for URL edit mode */}
      {showHeader && isUrlEditMode && (
        <PageTitleContainer
          ref={titleHeaderRef}
          title="Edit project"
          sx={{
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            ml: 0,
            mr: 0,
            mb: 2,
            mt: 0,
            py: '16px'
          }}
          titleProps={{
            sx: { fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }
          }}
        />
      )}

      {/* Just show close button if in edit mode with showHeader true */}
      {editMode && onClose && showHeader && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          p: 2,
          width: '100%'
        }}>
          <IconButton onClick={() => onClose && onClose(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* Main Content */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', pt: 0, width: '100%' }}>
        {showRepositorySelection ? (
          <RepositorySelectionStep
            projectId={createdProjectId}
            workflowType={workflowMode}
            onPrevious={() => {
              // Go back to project creation form
              setShowRepositorySelection(false);
              setCreatedProjectId(null);
            }}
            onNext={() => {
              // After repository selection, show task selection
              setShowRepositorySelection(false);
              setShowTaskSelection(true);
            }}
            onSkip={() => {
              // Skip repository selection and go to task selection
              setShowRepositorySelection(false);
              setShowTaskSelection(true);
            }}
          />
        ) : showTaskSelection ? (
          <Paper
            elevation={0}
            ref={contentRef}
            sx={{
              borderRadius: '4px',
              p: { xs: 2, sm: "12px 0px 12px 12px" },
              width: '100%',
              height: contentHeight,
              bgcolor: 'white',
              overflowX: 'hidden',
              overflowY: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              backgroundImage: 'none',
              '&::before': {
                display: 'none'
              },
              '&::after': {
                display: 'none'
              }
            }}
          >
            {/* Heading removed to avoid duplication */}

            <SectionedTaskSelector
              selectedTasks={selectedTasks}
              onTaskSelection={handleTaskSelection}
              onSelectAll={handleSelectAll}
              allTaskIds={allTaskIds}
              onVisibleTasksChange={setVisibleTaskIds}
              onSave={handleFinishTaskSelection}
              onSkip={() => navigate('/projects')}
              saveButtonText="Activate selected topics"
              showSaveButton={true}
              showContinueButton={false}
              showSkipButton={true}
              mode="selection"
              hideEmptySections={true}
              projectId={createdProjectId} // Pass the created project ID for orchestrator selection
              isDialog={true} // Add this to ensure no duplicate select all checkbox
              applicationType={applicationType} // Pass application type for filtering
            />
          </Paper>
        ) : (
          <Paper
            elevation={0}
            ref={contentRef}
            sx={{
              borderRadius: '4px',
              p: { xs: 2, sm: 3, md: 4, lg: '20px' },
              width: '100%',
              height: contentHeight,
              bgcolor: 'white',
              overflowX: 'hidden',
              overflowY: 'auto',
              boxShadow: editMode ? 'none' : '0 4px 20px rgba(0,0,0,0.05)',
              backgroundImage: 'none',
              '&::before': {
                display: 'none'
              },
              '&::after': {
                display: 'none'
              },
              m: "0px !important"
            }}
          >
            {(error || projectError) && (
              <Alert
                severity="error"
                sx={{
                  mb: 4,
                  borderRadius: 1.5,
                  '& .MuiAlert-icon': {
                    color: '#f44336'
                  }
                }}
              >
                {error || projectError}
              </Alert>
            )}

            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: editMode ? 0 : { xs: 2, sm: 4, md: 4, lg: '40px' },
              overflow: 'hidden',
            }}>
              {/* Left section */}
              <Box sx={{ flex: 3, maxWidth: { xs: '100%', md: '75%' }, overflow: 'hidden', pr: { xs: 0, md: 4 } }}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="body1" sx={{ mb: 1, whiteSpace: 'nowrap', display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                      Short code
                    </Typography>
                    <TextField
                      className="single-line-field"
                      id="shortCode"
                      variant="outlined"
                      value={shortCode}
                      onChange={(e) => setShortCode(e.target.value.toUpperCase())}
                      placeholder="Auto"
                      inputProps={{ maxLength: 2 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '4px',
                          color: 'var(--color-blue, #475969)',
                          height: '45px',
                          transition: 'all 0.2s',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#475969',
                            borderWidth: '1px',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#475969',
                            borderWidth: '1px',
                          }
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#4759694d',
                          border: '1px solid #4759694d',
                          borderRadius: '4px',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '14px',
                          padding: '12px 14px',
                          color: 'var(--color-blue, #475969)',
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: '#a2abb3',
                          opacity: 0.7,
                          fontSize: '14px',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                      Project name <span style={{ color: '#F89939' }}>*</span>
                    </Typography>
                    <TextField
                      required
                      className="single-line-field"
                      id="projectName"
                      variant="outlined"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      error={!!nameError}
                      helperText={nameError}
                      disabled={loading}
                      placeholder="Enter project name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '4px',
                          color: 'var(--color-blue, #475969)',
                          height: '45px',
                          transition: 'all 0.2s',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#475969',
                            borderWidth: '1px',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#475969',
                            borderWidth: '1px',
                          },
                          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#f44336',
                          }
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#4759694d',
                          border: '1px solid #4759694d',
                          borderRadius: '4px',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '14px',
                          padding: '12px 14px',
                          color: 'var(--color-blue, #475969)',
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: '#a2abb3',
                          opacity: 0.7,
                          fontSize: '14px',
                        },
                        '& .MuiFormHelperText-root': {
                          marginLeft: '4px',
                          fontSize: '12px'
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                    Description
                  </Typography>
                  <Box sx={{
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    maxWidth: '100%',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.2s'
                  }}>
                    <RichTextEditor
                      value={projectDescription}
                      onChange={setProjectDescription}
                      placeholder="Enter project description"
                      disabled={loading}
                      minHeight="150px"
                      sx={{
                        '& .ck-editor__editable': {
                          minHeight: '150px',
                          p: 2,
                          fontFamily: 'inherit',
                          fontSize: '14px',
                          color: 'var(--color-blue, #475969)',
                          '& .ck-placeholder': {
                            color: '#a2abb3',
                            opacity: 0.7,
                            fontSize: '14px'
                          }
                        },
                        '& .ck-toolbar': {
                          border: 'none',
                          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                          backgroundColor: 'rgba(0, 0, 0, 0.02)'
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Vertical divider */}
              <Divider orientation="vertical" flexItem sx={{
                display: { xs: 'none', md: 'block' },
                mx: 2
              }} />

              {/* Right section */}
              <Box sx={{ flex: 1, maxWidth: { xs: '100%', md: '25%' }, overflow: 'hidden', pl: { xs: 0, md: 2 } }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                    Project owner <span style={{ color: '#F89939' }}>*</span>
                  </Typography>
                  <ReactSelect
                    className="basic-single"
                    classNamePrefix="select"
                    value={projectOwner ? users.find(u => u.value === projectOwner) : null}
                    onChange={(option) => {
                      setProjectOwner(option ? option.value : '');
                    }}
                    options={users}
                    placeholder={loadingUsers ? "Loading users..." : "Select project owner"}
                    isClearable={false}
                    isLoading={loadingUsers}
                    isDisabled={loadingUsers}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderRadius: '4px',
                        minHeight: '45px',
                        borderColor: ownerError ? '#f44336' : (state.isFocused ? '#475969' : '#4759694d'),
                        boxShadow: state.isFocused ? 'none !important' : 'none',
                        '&:hover': {
                          borderColor: ownerError ? '#f44336' : '#475969'
                        }
                      }),
                      singleValue: (baseStyles) => ({
                        ...baseStyles,
                        fontSize: "14px !important",
                        color: 'var(--color-blue, #475969)'
                      }),
                      placeholder: (baseStyles) => ({
                        ...baseStyles,
                        color: '#a2abb3',
                        opacity: 0.7,
                        fontSize: "14px !important"
                      }),
                      menu: (baseStyles) => ({
                        ...baseStyles,
                        zIndex: 9999
                      }),
                      menuPortal: (baseStyles) => ({
                        ...baseStyles,
                        zIndex: 9999
                      })
                    }}
                    menuPortalTarget={document.body}
                  />
                  {ownerError && (
                    <FormHelperText error sx={{ marginLeft: '4px', fontSize: '12px', mt: 0.5 }}>
                      {ownerError}
                    </FormHelperText>
                  )}
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                    Project co-owners
                  </Typography>
                  <ReactSelect
                    className="basic-multi-select"
                    classNamePrefix="select"
                    isMulti
                    value={projectCoOwners.map(ownerId => users.find(u => u.value === ownerId)).filter(Boolean)}
                    onChange={(selectedOptions) => {
                      const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
                      setProjectCoOwners(values);
                    }}
                    options={users}
                    placeholder={loadingUsers ? "Loading users..." : "Select co-owners"}
                    isLoading={loadingUsers}
                    isDisabled={loadingUsers}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderRadius: '4px',
                        minHeight: '45px',
                        borderColor: state.isFocused ? '#475969' : '#4759694d',
                        boxShadow: state.isFocused ? 'none !important' : 'none',
                        '&:hover': {
                          borderColor: '#475969'
                        }
                      }),
                      placeholder: (baseStyles) => ({
                        ...baseStyles,
                        color: '#a2abb3',
                        opacity: 0.7,
                        fontSize: "14px !important"
                      }),
                      multiValue: (baseStyles) => ({
                        ...baseStyles,
                        backgroundColor: '#e3f2fd',
                        borderRadius: '4px'
                      }),
                      singleValue: (baseStyles) => ({
                        ...baseStyles,
                        fontSize: "14px !important",
                        color: 'var(--color-blue, #475969)'
                      }),
                      menu: (baseStyles) => ({
                        ...baseStyles,
                        zIndex: 9999
                      }),
                      menuPortal: (baseStyles) => ({
                        ...baseStyles,
                        zIndex: 9999
                      })
                    }}
                    menuPortalTarget={document.body}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                    Teams
                  </Typography>
                  <ReactSelect
                    className="basic-single"
                    classNamePrefix="select"
                    value={team ? teams.find(t => t.value === team) : null}
                    onChange={(option) => setTeam(option ? option.value : '')}
                    options={teams}
                    placeholder={loadingTeams ? "Loading teams..." : "Select team"}
                    isClearable={true}
                    isLoading={loadingTeams}
                    isDisabled={loadingTeams}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderRadius: '4px',
                        minHeight: '45px',
                        borderColor: state.isFocused ? '#475969' : '#4759694d',
                        boxShadow: state.isFocused ? 'none !important' : 'none',
                        '&:hover': {
                          borderColor: '#475969'
                        }
                      }),
                      placeholder: (baseStyles) => ({
                        ...baseStyles,
                        color: '#a2abb3',
                        opacity: 0.7,
                        fontSize: "14px !important"
                      }),
                      singleValue: (baseStyles) => ({
                        ...baseStyles,
                        fontSize: "14px !important",
                        color: 'var(--color-blue, #475969)'
                      }),
                      menu: (baseStyles) => ({
                        ...baseStyles,
                        zIndex: 9999
                      }),
                      menuPortal: (baseStyles) => ({
                        ...baseStyles,
                        zIndex: 9999
                      })
                    }}
                    menuPortalTarget={document.body}
                  />
                </Box>

                {/* Status and PRD Creation Mode fields removed */}

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                    Application Type
                  </Typography>
                  <ReactSelect
                    className="basic-single"
                    classNamePrefix="select"
                    value={applicationType ? applicationTypes.find(t => t.id === applicationType) : null}
                    onChange={(option) => setApplicationType(option ? option.id : '')}
                    options={applicationTypes}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    placeholder="Select application type"
                    isClearable={true}
                    isDisabled={loading}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderRadius: '4px',
                        minHeight: '45px',
                        borderColor: state.isFocused ? '#475969' : '#4759694d',
                        boxShadow: state.isFocused ? 'none !important' : 'none',
                        '&:hover': {
                          borderColor: '#475969'
                        }
                      }),
                      placeholder: (baseStyles) => ({
                        ...baseStyles,
                        color: '#a2abb3',
                        opacity: 0.7,
                        fontSize: "14px !important"
                      }),
                      singleValue: (baseStyles) => ({
                        ...baseStyles,
                        fontSize: "14px !important",
                        color: 'var(--color-blue, #475969)'
                      }),
                      menu: (baseStyles) => ({
                        ...baseStyles,
                        zIndex: 9999
                      }),
                      menuPortal: (baseStyles) => ({
                        ...baseStyles,
                        zIndex: 9999
                      })
                    }}
                    menuPortalTarget={document.body}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    component="label"
                    variant="body1"
                    sx={{
                      mb: 1,
                      display: 'block',
                      fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                      fontWeight: 700
                    }}
                  >
                    Distribution email
                  </Typography>
                  <TextField
                    className="single-line-field"
                    id="distributionEmail"
                    variant="outlined"
                    value={distributionEmail}
                    onChange={(e) => setDistributionEmail(e.target.value)}
                    disabled={loading}
                    placeholder="Enter distribution email"
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        color: 'var(--color-blue, #475969)',
                        height: '45px',
                        transition: 'all 0.2s',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#475969',
                          borderWidth: '1px',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#475969',
                          borderWidth: '1px',
                        },
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4759694d',
                        border: '1px solid #4759694d',
                        borderRadius: '4px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '12px 14px',
                        fontSize: '14px',
                        color: 'var(--color-blue, #475969)',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#a2abb3',
                        opacity: 0.7,
                        fontSize: '14px',
                      }
                    }}
                  />
                </Box>

              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, pb: 1 }}>
              <Button
                variant="contained"
                onClick={handleCreateProject}
                disabled={loading}
                sx={{
                  bgcolor: '#485869',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#3a4a59',
                  },
                  borderRadius: '5px',
                  px: 5,
                  py: 0.5,
                  minWidth: '120px',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                }}
              >
                {loading ?
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} /> {editMode ? 'Updating...' : 'Creating...'}
                  </Box> :
                  'Save'
                }
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Avatar Selection Dialog removed */}
    </Box>
  );
};

export default CreateProject;